import { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CoachData, BatchPlannedQuestion } from '../QuestionCoachModal';
import { useGetFeedTeamsQuery } from '@/api/feed';
import { BATCH_QUESTION_WARNING_THRESHOLD } from '@/constants/question-coach';
import { CheckCircle2, AlertTriangle } from 'lucide-react';

interface ReviewStepProps {
  coachData: CoachData;
  /** Batch creation mode (DKPLAT-501 Phase 1): the flattened fixture x team plan. */
  batchPlan?: BatchPlannedQuestion[];
}

const QUESTION_TYPE_LABELS: Record<string, string> = {
  player_selection: 'Player Selection',
  team_selection: 'Team Selection',
  player_over_under: 'Player Over/Under',
  team_over_under: 'Team Over/Under',
  // Main markets (DKPLAT-500)
  moneyline: 'Moneyline',
  spread: 'Spread',
  total: 'Total',
};

export function ReviewStep({ coachData, batchPlan }: ReviewStepProps) {
  const { data: teams } = useGetFeedTeamsQuery(batchPlan ? coachData.leagueId : undefined);

  const teamMap = useMemo(() => {
    const map = new Map<string, string>();
    teams?.forEach(team => map.set(team.externalId, team.name));
    return map;
  }, [teams]);

  if (batchPlan) {
    const count = batchPlan.length;
    const label = QUESTION_TYPE_LABELS[coachData.questionType || ''] || 'Question';

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-green-600">
          <CheckCircle2 className="h-5 w-5" />
          <span className="font-semibold">
            Review the {count} planned question{count === 1 ? '' : 's'} before creating
          </span>
        </div>

        {count > BATCH_QUESTION_WARNING_THRESHOLD && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              You&apos;re about to create {count} questions — continue?
            </AlertDescription>
          </Alert>
        )}

        <div>
          <h4 className="font-semibold mb-2">Question Details</h4>
          <Separator className="mb-3" />
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Question Type:</span>
              <Badge variant="outline">{label}</Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Sport:</span>
              <span className="font-medium">{coachData.sportName || 'Not set'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">League:</span>
              <span className="font-medium">{coachData.leagueName || 'Not set'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Stat Type:</span>
              <span className="font-medium">{coachData.statTypeName || 'Not set'}</span>
            </div>
          </div>
        </div>

        <div>
          <h4 className="font-semibold mb-2">Planned Questions ({count})</h4>
          <Separator className="mb-3" />
          {count === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing to create yet.</p>
          ) : (
            <div className="space-y-2 max-h-[350px] overflow-y-auto">
              {batchPlan.map((item, index) => (
                <Card key={item.key} className="p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{index + 1}</Badge>
                      <div>
                        <div className="text-sm font-medium">{item.fixtureDisplay}</div>
                        {item.teamId && (
                          <div className="text-xs text-muted-foreground">
                            {teamMap.get(item.teamId) || item.teamId}
                          </div>
                        )}
                        {item.playerName && (
                          <div className="text-xs text-muted-foreground">{item.playerName}</div>
                        )}
                        {item.teamIds && item.teamIds.length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            {item.teamIds.map(id => teamMap.get(id) || id).join(' vs ')}
                          </div>
                        )}
                        {item.playerNames && item.playerNames.length > 0 && (
                          <div className="text-xs text-muted-foreground">
                            {item.playerNames.join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                    {item.margin !== undefined && (
                      <Badge variant="outline">Line: {item.margin}</Badge>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        <Card className="p-4 bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
          <p className="text-sm">
            <strong>Note:</strong> The backend will automatically generate question text in both
            English and Spanish, populate correct answers, and assign points for each question.
            Questions are created one at a time — if a request fails you can retry just that row.
          </p>
        </Card>
      </div>
    );
  }

  const isOverUnder =
    coachData.questionType === 'player_over_under' || coachData.questionType === 'team_over_under';

  // Main markets that carry an admin-entered half-point line (DKPLAT-500).
  const isMainMarketLine =
    coachData.questionType === 'spread' || coachData.questionType === 'total';

  const isPlayerQuestion =
    coachData.questionType === 'player_selection' || coachData.questionType === 'player_over_under';

  // Generate a preview question text
  const generatePreviewText = () => {
    if (!coachData.questionType || !coachData.statTypeName) return 'Question preview unavailable';

    if (coachData.questionType === 'player_selection') {
      return `Which player will have the most ${coachData.statTypeName?.toLowerCase()}?`;
    }

    if (coachData.questionType === 'team_selection') {
      return `Which team will have the most ${coachData.statTypeName?.toLowerCase()}?`;
    }

    if (coachData.questionType === 'player_over_under') {
      const playerName = coachData.playerNames?.[0] || 'player';
      const threshold = coachData.margin || 'X';
      return `Will ${playerName} have over ${threshold} ${coachData.statTypeName?.toLowerCase()}?`;
    }

    if (coachData.questionType === 'team_over_under') {
      const teamName = coachData.teamNames?.[0] || 'team';
      const threshold = coachData.margin || 'X';
      return `Will ${teamName} have over ${threshold} ${coachData.statTypeName?.toLowerCase()}?`;
    }

    // Main markets (DKPLAT-500)
    if (coachData.questionType === 'moneyline') {
      return 'Which team will win?';
    }
    if (coachData.questionType === 'spread') {
      const fav = coachData.teamNames?.[0];
      const line = coachData.margin ?? 'X';
      return fav
        ? `Which team will cover the spread? (${fav} -${line})`
        : 'Which team will cover the spread?';
    }
    if (coachData.questionType === 'total') {
      const line = coachData.margin ?? 'X';
      return `Will over ${line} total ${coachData.statTypeName?.toLowerCase()} be scored in the match?`;
    }

    return 'Question preview unavailable';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-green-600">
        <CheckCircle2 className="h-5 w-5" />
        <span className="font-semibold">Review your question before creating</span>
      </div>

      <Card className="p-6 bg-primary/5">
        <h3 className="font-semibold text-lg mb-2">Question Preview (English)</h3>
        <p className="text-base">{generatePreviewText()}</p>
      </Card>

      <div className="space-y-4">
        <div>
          <h4 className="font-semibold mb-2">Question Details</h4>
          <Separator className="mb-3" />

          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Question Type:</span>
              <Badge variant="outline">
                {coachData.questionType ? QUESTION_TYPE_LABELS[coachData.questionType] : 'Not set'}
              </Badge>
            </div>

            <div className="flex justify-between">
              <span className="text-muted-foreground">Sport:</span>
              <span className="font-medium">{coachData.sportName || 'Not set'}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-muted-foreground">League:</span>
              <span className="font-medium">{coachData.leagueName || 'Not set'}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-muted-foreground">Fixture:</span>
              <span className="font-medium text-right max-w-[60%]">
                {coachData.fixtureDisplay || 'Not set'}
              </span>
            </div>

            {coachData.questionType !== 'moneyline' && coachData.questionType !== 'spread' && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Stat Type:</span>
                <span className="font-medium">{coachData.statTypeName || 'Not set'}</span>
              </div>
            )}

            {isOverUnder && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Threshold:</span>
                <span className="font-medium">{coachData.margin || 'Not set'}</span>
              </div>
            )}

            {isMainMarketLine && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Line:</span>
                <span className="font-medium">{coachData.margin ?? 'Not set'}</span>
              </div>
            )}
          </div>
        </div>

        <div>
          <h4 className="font-semibold mb-2">
            {isPlayerQuestion ? 'Selected Players' : 'Selected Teams'}
          </h4>
          <Separator className="mb-3" />

          {isPlayerQuestion ? (
            <div className="space-y-2">
              {coachData.playerNames && coachData.playerNames.length > 0 ? (
                coachData.playerNames.map((name, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Badge variant="secondary">{index + 1}</Badge>
                    <span>{name}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No players selected</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {coachData.teamNames && coachData.teamNames.length > 0 ? (
                coachData.teamNames.map((name, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Badge variant="secondary">{index + 1}</Badge>
                    <span>{name}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">No teams selected</p>
              )}
            </div>
          )}
        </div>
      </div>

      <Card className="p-4 bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
        <p className="text-sm">
          <strong>Note:</strong> The backend will automatically generate question text in both
          English and Spanish, populate correct answers, and assign points based on your selections.
        </p>
      </Card>
    </div>
  );
}
